import React from 'react';
import { Users, Star, Phone, Mail, MessageCircle, TrendingUp, CheckCheck } from 'lucide-react';

const AgentSection = ({ agents = [], onContactAgent, detailed = false }) => {
  if (agents?.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Users className="w-5 h-5 mr-2" />
            Agents assignés
          </h3>
        </div>
        <div className="text-center py-8">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Aucun agent assigné pour le moment</p>
        </div>
      </div>
    );
  }

  const primaryAgent = agents?.find(a => a?.is_primary);

  if (!detailed && !primaryAgent) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Users className="w-5 h-5 mr-2" />
            Agents assignés ({agents?.length})
          </h3>
        </div>
        <div className="space-y-3">
          {agents?.slice(0, 3)?.map((assignment) => (
            <div key={assignment?.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <img
                src={assignment?.agent?.user_profiles?.avatar_url || `/api/placeholder/40/40`}
                alt={assignment?.agent?.user_profiles?.full_name}
                className="w-10 h-10 rounded-full"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {assignment?.agent?.user_profiles?.full_name}
                </p>
                <p className="text-xs text-gray-500">
                  {assignment?.specialization || 'Agent musical'}
                </p>
              </div>
              {assignment?.is_primary && (
                <Star className="w-4 h-4 text-yellow-400 fill-current" />
              )}
            </div>
          ))}
          {agents?.length > 3 && (
            <p className="text-sm text-gray-500 text-center">
              +{agents?.length - 3} autre{agents?.length - 3 > 1 ? 's' : ''} agent{agents?.length - 3 > 1 ? 's' : ''}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Users className="w-5 h-5 mr-2" />
          {detailed ? `Agents assignés (${agents?.length})` : 'Agent principal'}
        </h3>
        {!detailed && agents?.length > 1 && (
          <span className="text-sm text-gray-500">+{agents?.length - 1} autres</span>
        )}
      </div>
      <div className="space-y-6">
        {(detailed ? agents : [primaryAgent || agents?.[0]])?.map((assignment) => (
          <div key={assignment?.id} className="border border-gray-200 rounded-lg p-4">
            {/* Agent Header */}
            <div className="flex items-start space-x-4 mb-4">
              <img
                src={assignment?.agent?.user_profiles?.avatar_url || `/api/placeholder/64/64`}
                alt={assignment?.agent?.user_profiles?.full_name}
                className="w-16 h-16 rounded-full"
              />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-medium text-gray-900">
                    {assignment?.agent?.user_profiles?.full_name}
                  </h4>
                  <div className="flex items-center space-x-2">
                    {assignment?.is_primary && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        <Star className="w-3 h-3 mr-1 fill-current" />
                        Principal
                      </span>
                    )}
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      assignment?.status === 'active' ?'bg-green-100 text-green-800' :'bg-gray-100 text-gray-800'
                    }`}>
                      {assignment?.status === 'active' ? 'Actif' : 'Inactif'}
                    </span>
                  </div>
                </div>
                <p className="text-gray-600 mb-2">{assignment?.agent?.business_name}</p>
                <p className="text-sm text-gray-500">{assignment?.specialization}</p>
                
                {/* Agent Rating */}
                {assignment?.agent?.average_rating > 0 && (
                  <div className="flex items-center mt-2">
                    <Star className="w-4 h-4 text-yellow-400 fill-current" />
                    <span className="ml-1 text-sm text-gray-600">
                      {assignment?.agent?.average_rating}
                    </span>
                    <span className="ml-1 text-sm text-gray-500">
                      ({assignment?.agent?.total_bookings} réservations)
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Performance Metrics */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center">
                  <TrendingUp className="w-4 h-4 text-green-500 mr-2" />
                  <div>
                    <div className="text-lg font-semibold text-gray-900">
                      {assignment?.booking_success_rate || 0}%
                    </div>
                    <div className="text-xs text-gray-500">Taux de réussite</div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center">
                  <CheckCheck className="w-4 h-4 text-blue-500 mr-2" />
                  <div>
                    <div className="text-lg font-semibold text-gray-900">
                      {assignment?.commission_rate || 10}%
                    </div>
                    <div className="text-xs text-gray-500">Commission</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="border-t border-gray-200 pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {assignment?.agent?.phone && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Phone className="w-4 h-4 mr-1" />
                      {assignment?.agent?.phone}
                    </div>
                  )}
                  {assignment?.agent?.email && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Mail className="w-4 h-4 mr-1" />
                      {assignment?.agent?.email}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => onContactAgent?.(assignment?.agent?.id)}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 transition-colors"
                >
                  <MessageCircle className="w-3 h-3 mr-1" />
                  Contacter
                </button>
              </div>
            </div>

            {/* Assignment Date */}
            <div className="mt-3 text-xs text-gray-500">
              Assigné le {new Date(assignment?.assigned_at)?.toLocaleDateString('fr-FR')}
            </div>
          </div>
        ))}
      </div>
      {!detailed && agents?.length > 1 && (
        <div className="mt-4 text-center">
          <button className="text-blue-600 hover:text-blue-500 text-sm font-medium">
            Voir tous les agents ({agents?.length})
          </button>
        </div>
      )}
    </div>
  );
};

export default AgentSection;